import { useState, useCallback, useRef } from 'react';

/**
 * useLocalState — localStorage に永続化される useState 互換 hook
 *
 * v2 (2026-04-30): key 変更時のレース条件を解消
 *   - 旧: render 中に setValueState 呼び → 1フレーム古い値を返してしまい、
 *         consumer の useEffect が「キャッシュなし」と誤判定して fetch を起動 → 上書き発生
 *   - 新: state を { key, value } の組で管理し、key 不一致時は新値を即座に
 *         同期計算して return。React の "deriving state from props" 正規パターン
 *
 * @param {string} key      localStorage のキー (一意)。null/undefined なら storage 不使用
 * @param {*}      initial  初期値 (関数も可)
 * @param {object} [options]
 * @param {number} [options.ttlDays=30]  有効期限 (日数)。0 で無期限
 * @returns {[value, setValue, clear]}
 */
const memoryCache = new Map();
const MS_PER_DAY = 86400_000;

function readEntry(key, ttlDays) {
  if (key == null) return undefined;
  if (memoryCache.has(key)) return memoryCache.get(key);
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && '__ls_v' in parsed) {
      const age = Date.now() - (parsed.__ls_t || 0);
      if (ttlDays > 0 && age > ttlDays * MS_PER_DAY) {
        try { localStorage.removeItem(key); } catch {}
        return undefined;
      }
      memoryCache.set(key, parsed.__ls_v);
      return parsed.__ls_v;
    }
    // 旧形式 (生の値) — マイグレーション
    memoryCache.set(key, parsed);
    return parsed;
  } catch {
    return undefined;
  }
}

// ★ iter 25 (2026-05-14): localStorage 同期書込最適化
//   memoryCache は同期書込で「即時 read を新値で返す」 を保証。
//   localStorage への persist は requestIdleCallback / microtask で defer → 連続書込時の main thread block を解消。
//   pending 中に同じ key への書込があれば最新値で上書き (= 1 key につき 1 write に集約)。
const _pendingWrites = new Map(); // key → latest value
let _flushScheduled = false;
const _scheduleIdle = (cb) => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(cb, { timeout: 1000 });
  } else {
    setTimeout(cb, 0);
  }
};
function _flushPendingWrites() {
  _flushScheduled = false;
  if (_pendingWrites.size === 0) return;
  const entries = Array.from(_pendingWrites.entries());
  _pendingWrites.clear();
  for (const [k, v] of entries) {
    try {
      const wrapped = { __ls_v: v, __ls_t: Date.now() };
      localStorage.setItem(k, JSON.stringify(wrapped));
    } catch {
      // quota exceeded etc.
    }
  }
}
// page unload で必ず flush (= データ損失防止)
if (typeof window !== 'undefined' && !window.__useLocalState_unload_bound) {
  window.__useLocalState_unload_bound = true;
  // beforeunload で同期 flush (idle callback では間に合わない可能性)
  window.addEventListener('beforeunload', () => {
    _flushPendingWrites();
  });
  // visibility 変化でも flush (= モバイルで beforeunload が来ない場合のセーフティ)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') _flushPendingWrites();
  });
}

function writeEntry(key, value) {
  if (key == null) return;
  memoryCache.set(key, value);
  _pendingWrites.set(key, value);
  if (!_flushScheduled) {
    _flushScheduled = true;
    _scheduleIdle(_flushPendingWrites);
  }
}

// 強制 flush API (テスト / 手動制御用)
export function flushLocalStateWrites() {
  _flushPendingWrites();
}

export function useLocalState(key, initial, options = {}) {
  const ttlDays = options.ttlDays != null ? options.ttlDays : 30;
  const initialRef = useRef(initial);
  initialRef.current = initial;

  const computeFromStorage = useCallback((k) => {
    if (k == null) return typeof initialRef.current === 'function' ? initialRef.current() : initialRef.current;
    const s = readEntry(k, ttlDays);
    if (s !== undefined) return s;
    return typeof initialRef.current === 'function' ? initialRef.current() : initialRef.current;
  }, [ttlDays]);

  // state を { key, value } の組で持つ → key 変更を検出可能
  const [state, setState] = useState(() => ({ key, value: computeFromStorage(key) }));

  // ★ React 公式パターン: render 中に key 不一致を検出したら、
  //    新しい value を**同期計算して return** + 並行して setState で次回に反映
  //    これで consumer は同じ render 内で正しい値を受け取れる
  let currentValue = state.value;
  if (state.key !== key) {
    currentValue = computeFromStorage(key);
    setState({ key, value: currentValue });
  }

  const setValue = useCallback((next) => {
    setState(prev => {
      const v = typeof next === 'function' ? next(prev.value) : next;
      writeEntry(prev.key, v);
      return { key: prev.key, value: v };
    });
  }, []);

  const clear = useCallback(() => {
    setState(prev => {
      if (prev.key != null) {
        memoryCache.delete(prev.key);
        try { localStorage.removeItem(prev.key); } catch {}
      }
      const v = typeof initialRef.current === 'function' ? initialRef.current() : initialRef.current;
      return { key: prev.key, value: v };
    });
  }, []);

  return [currentValue, setValue, clear];
}

/**
 * プレフィックス一致で一括削除
 */
export function clearLocalStateByPrefix(prefix) {
  const toDelete = [];
  for (const k of memoryCache.keys()) if (k && k.startsWith(prefix)) toDelete.push(k);
  for (const k of toDelete) memoryCache.delete(k);
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch {}
}

/**
 * 起動時の掃除: 期限切れエントリを一括削除
 *
 * @param {number} ttlDays  この日数を超えたエントリを削除 (デフォルト 30)
 * @param {string} [prefix] 対象キーのプレフィックス (空なら全件)
 */
export function purgeExpiredLocalState(ttlDays = 30, prefix = '') {
  if (ttlDays <= 0) return 0;
  const cutoff = Date.now() - ttlDays * MS_PER_DAY;
  let purged = 0;
  try {
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (prefix && !k.startsWith(prefix)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && '__ls_t' in parsed) {
          if (parsed.__ls_t < cutoff) toDelete.push(k);
        }
      } catch { /* 不正な値はスキップ */ }
    }
    for (const k of toDelete) {
      localStorage.removeItem(k);
      memoryCache.delete(k);
      purged++;
    }
  } catch {}
  return purged;
}

export default useLocalState;
